// src/components/sops/sopsApi.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
//
// Everything the SOP screens send or read. Every change goes through a Seam 1 server function
// (sopslib-4c9e2a71); the reads are the ones firestore.rules allow an active Owner / Admin / Manager
// (folders, SOPs, versions, access records). One-time reads only — no live listeners — and each screen
// reloads after a change. The SOP file itself is never read from Storage: openSop hands back a
// 60-second link.

import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { db, functions, storage } from '../../firebase/config';
import { chunks } from '../../utils/sopsUi';

export { friendlyError } from '../settings/catalog/catalogApi';

const call = async (name, data) => (await httpsCallable(functions, name)(data)).data;
const col = (orgId, name) => collection(db, `organizations/${orgId}/${name}`);
const withId = (d) => ({ id: d.id, ...d.data() });

// ─── reads ──────────────────────────────────────────────────────────────────
// Library: one read per folder + one per SOP (the counts live on each SOP — no per-SOP lookups).
export const loadFolders = async (orgId) => (await getDocs(col(orgId, 'sopFolders'))).docs.map(withId);
export const loadSops = async (orgId) => (await getDocs(col(orgId, 'sops'))).docs.map(withId);
export async function loadSop(orgId, sopId) {
    const s = await getDoc(doc(db, `organizations/${orgId}/sops/${sopId}`));
    return s.exists() ? withId(s) : null;
}
export const loadVersions = async (orgId, sopId) => (await getDocs(collection(db, `organizations/${orgId}/sops/${sopId}/versions`))).docs
    .map(withId).sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
export const loadAccess = async (orgId, sopId) => (await getDocs(query(col(orgId, 'sopAccess'), where('sopId', '==', sopId)))).docs.map((d) => d.data());

// sopread-7e4c2a93 (Seam 3) — My SOPs: ONE query of the person's own access records (title, folder,
// version and confirm type are copied on). firestore.rules let anyone read their own records only when
// the query also filters sopArchived == false, so both filters are always sent.
export const myAccessQuery = (orgId, uid) => query(col(orgId, 'sopAccess'), where('uid', '==', uid), where('sopArchived', '==', false));
export const loadMyAccess = async (orgId, uid) => (await getDocs(myAccessQuery(orgId, uid))).docs.map((d) => d.data());

/** "I've read and understand this" / the typed-name signature, for the version the screen shows. */
export const confirmRead = (sopId, versionId, typedName) => call('confirmSopRead', typedName === undefined
    ? { sopId, versionId, client: 'web' }
    : { sopId, versionId, typedName, client: 'web' });

/** Catalog item names for the ids linked to an SOP — one read per linked item (at most 50), never the whole catalog. */
export async function loadCatalogNames(orgId, ids) {
    const snaps = await Promise.all((ids || []).map((id) => getDoc(doc(db, `organizations/${orgId}/services/${id}`)).catch(() => null)));
    return (ids || []).map((id, i) => {
        const s = snaps[i];
        return { id, name: s && s.exists() ? s.data().name || 'Unnamed item' : 'Item no longer in the catalog', missing: !(s && s.exists()) };
    });
}

// ─── folders ────────────────────────────────────────────────────────────────
export const createFolder = (name, parentId) => call('createSopFolder', { name, parentId: parentId || null });
export const renameFolder = (folderId, name) => call('renameSopFolder', { folderId, name });
export const moveFolder = (folderId, parentId) => call('moveSopFolder', { folderId, parentId: parentId || null });
export const archiveFolder = (folderId, archived) => call('archiveSopFolder', { folderId, archived });

// ─── SOPs ───────────────────────────────────────────────────────────────────
export const updateDetails = (sopId, fields) => call('updateSopDetails', { sopId, ...fields });
export const archiveSop = (sopId, archived, folderId) => call('archiveSop', folderId ? { sopId, archived, folderId } : { sopId, archived });

/** Save people in batches of 200 (the server's limit). */
export async function saveAccess(sopId, people) {
    for (const part of chunks(people)) await call('setSopAccess', { sopId, people: part });
}

/** The browser's upload to the drop box the server named. onProgress(0..1). */
function uploadToSlot(file, path, onProgress) {
    return new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef(storage, path), file, { contentType: 'application/pdf' });
        task.on('state_changed',
            (s) => onProgress && s.totalBytes && onProgress(s.bytesTransferred / s.totalBytes),
            reject,
            resolve);
    });
}

/**
 * Upload a file: ask for a slot → upload → finish. onStep('upload', fraction) | onStep('check').
 *   target: { details } for a new SOP, or { sopId } for a new version.
 */
async function uploadAndFinish(file, target, finishExtra, onStep) {
    const slot = await call('createSopUploadSlot', { fileName: file.name, sizeBytes: file.size, ...target });
    await uploadToSlot(file, slot.uploadPath, (f) => onStep && onStep('upload', f));
    if (onStep) onStep('check');
    return call('finishSopUpload', { uploadId: slot.uploadId, ...finishExtra });
}
export const createSopWithFile = (file, details, onStep) => uploadAndFinish(file, { details }, {}, onStep);
export const replaceSopFile = (file, sopId, { changeNote, notifyPeople }, onStep) => uploadAndFinish(file, { sopId }, { changeNote, notifyPeople }, onStep);

// ─── open ───────────────────────────────────────────────────────────────────
/**
 * Open the PDF in a new tab. The tab is opened straight away (while the click still counts, so
 * pop-up blockers allow it), shows "Opening…", then goes to the 60-second link. Throws a plain
 * sentence when the browser blocks the tab or the server refuses.
 */
export async function openSopInNewTab(sopId, versionId) {
    const tab = window.open('', '_blank');
    if (!tab) throw new Error('Your browser blocked the new tab. Allow pop-ups for Spatchy, then try again.');
    try {
        tab.document.title = 'Opening SOP…';
        tab.document.body.innerHTML = '<p style="font-family:system-ui,sans-serif;color:#12192D;padding:24px">Opening the SOP…</p>';
    } catch (e) { /* the page may not be ready yet — the link below still loads */ }
    try {
        const res = await call('openSop', versionId ? { sopId, versionId, client: 'web' } : { sopId, client: 'web' });
        tab.opener = null;
        tab.location.replace(res.url);
        return res;
    } catch (e) {
        tab.close();
        throw e;
    }
}

/**
 * Download (save) the PDF — openSop mode "download": the same access checks, a fresh 60-second link
 * whose file comes as an attachment named "<title> – v<N>.pdf". Followed straight away by a plain
 * link click (no new tab, no fetch — the house signed-URL pattern), so the page stays where it is.
 * Chrome's own viewer download button can't do this: it re-asks for the viewing link after it expired.
 */
export async function downloadSop(sopId, versionId) {
    const res = await call('openSop', versionId ? { sopId, versionId, client: 'web', mode: 'download' } : { sopId, client: 'web', mode: 'download' });
    const a = document.createElement('a');
    a.href = res.url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return res;
}
